import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BadgeCheck, Globe, Mail, Phone, Loader2, MapPin, Factory, ShieldCheck, Undo2 } from 'lucide-react';
import { market } from '../lib/api';
import CatalogCard from '../components/CatalogCard';

function Header({ x, isBrand }) {
  return (
    <div className="rounded-2xl overflow-hidden border border-border bg-white">
      <div className="h-36 md:h-48 bg-gradient-to-r from-[#FFE0B2] to-[#FFF8EE]" style={x.banner_url ? { backgroundImage: `url(${x.banner_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}/>
      <div className="p-5 flex flex-wrap items-center gap-4 -mt-10">
        <span className="w-20 h-20 rounded-2xl bg-white border-4 border-white shadow grid place-items-center overflow-hidden">
          {x.logo_url ? <img src={x.logo_url} alt="" className="w-full h-full object-cover"/> : isBrand ? <b className="text-3xl text-[#D35400]">{x.name[0]}</b> : <Factory className="w-8 h-8 text-[#D35400]"/>}
        </span>
        <div className="flex-1 min-w-[200px] pt-8">
          <h1 data-testid="storefront-name" className="font-heading font-black text-2xl md:text-3xl">{x.name}</h1>
          <div className="flex flex-wrap gap-2 mt-1 text-xs">
            <span data-testid="storefront-verified" className="inline-flex items-center gap-1 rounded-full bg-[#1B5E20] text-white px-2.5 py-1 font-bold"><BadgeCheck className="w-3.5 h-3.5"/>{isBrand && x.official ? 'Official brand store' : 'Verified by BharatCart'}</span>
            {!isBrand && <span className="pill"><MapPin className="w-3 h-3"/>{x.city}, {x.state}</span>}
            {!isBrand && x.udyam && <span className="pill">MSME registered</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-3 text-sm pt-8">
          {x.website && <a href={x.website.startsWith('http') ? x.website : `https://${x.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline"><Globe className="w-4 h-4"/>Website</a>}
          {x.contact_email && <a href={`mailto:${x.contact_email}`} className="inline-flex items-center gap-1 underline"><Mail className="w-4 h-4"/>Email</a>}
          {x.contact_phone && <a href={`tel:${x.contact_phone}`} className="inline-flex items-center gap-1 underline"><Phone className="w-4 h-4"/>Call</a>}
        </div>
      </div>
    </div>
  );
}

export default function Storefront({ kind }) {
  const { slug } = useParams();
  const isBrand = kind === 'brand';
  const [x, setX] = useState(null);
  const [err, setErr] = useState(false);
  const [q, setQ] = useState('');
  const load = (query) => (isBrand ? market.brand(slug, query ? { q: query } : {}) : market.manufacturer(slug)).then(setX).catch(() => setErr(true));
  useEffect(() => { setX(null); load(''); }, [slug, isBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <div className="p-10 text-center" data-testid="storefront-missing">Not found.</div>;
  if (!x) return <Loader2 className="w-6 h-6 animate-spin m-10 text-[#E67E22]"/>;
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6" data-testid={`storefront-${kind}`}>
      <Header x={x} isBrand={isBrand}/>
      {(x.description || x.history) && (
        <section className="grid md:grid-cols-2 gap-4 text-sm text-ink/80">
          {x.description && <div><div className="font-heading font-bold text-base text-ink mb-1">About</div><p className="whitespace-pre-line">{x.description}</p></div>}
          {x.history && <div><div className="font-heading font-bold text-base text-ink mb-1">Our story</div><p className="whitespace-pre-line">{x.history}</p></div>}
        </section>
      )}
      {isBrand && (x.warranty_info || x.return_info) && (
        <section className="grid md:grid-cols-2 gap-3 text-sm">
          {x.warranty_info && <div className="card-soft p-3"><b className="flex items-center gap-1"><ShieldCheck className="w-4 h-4"/>Warranty</b>{x.warranty_info}</div>}
          {x.return_info && <div className="card-soft p-3"><b className="flex items-center gap-1"><Undo2 className="w-4 h-4"/>Returns</b>{x.return_info}</div>}
        </section>
      )}
      {!isBrand && x.photos?.length > 0 && (
        <div className="flex gap-2 overflow-x-auto">{x.photos.map(u => <img key={u} src={u} alt="Factory" className="h-32 rounded-xl object-cover"/>)}</div>
      )}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="font-heading font-bold text-xl">Products</div>
          {isBrand && (
            <form onSubmit={e => { e.preventDefault(); load(q.trim()); }} className="flex gap-2">
              <input data-testid="storefront-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder={`Search in ${x.name}`} className="rounded-full border border-border px-4 py-1.5 text-sm"/>
              <button data-testid="storefront-search-btn" className="btn-outline !py-1.5 text-sm">Search</button>
            </form>
          )}
        </div>
        {x.products.length === 0 ? <p className="text-sm text-ink/60" data-testid="storefront-no-products">{q.trim() ? 'No products match your search.' : 'No products listed yet.'}</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">{x.products.map(p => <CatalogCard key={p.id} p={p}/>)}</div>
        )}
      </section>
    </div>
  );
}
